import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatWIB } from "@/lib/time";
import { STATUS_PALETTE, PALETTE_KEYS, invalidateStatusReference } from "@/lib/statusTheme";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Save, Shield, Database, Loader2, ScrollText, Lock, GripVertical, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import AttendanceLocationMap from "@/components/AttendanceLocationMap";
import { CSS } from "@dnd-kit/utilities";

const inputCls = "w-full h-10 px-3 rounded-md border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent text-sm";

function SortableMasterRow({ item, testkey, showColor, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? "#F8F9FA" : undefined,
  };
  const swatch = showColor ? (STATUS_PALETTE[item.color] || STATUS_PALETTE.slate) : null;
  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-t border-[#E5E7EB]"
      data-testid={`master-${testkey}-row-${item.code}`}
    >
      <td className="pl-3 pr-1 py-2 w-8">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="h-7 w-7 rounded-md text-[#9CA3AF] hover:text-[#0A0A0A] hover:bg-[#F3F4F6] flex items-center justify-center cursor-grab active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]"
          aria-label={`Seret untuk mengubah urutan ${item.code}`}
          data-testid={`master-${testkey}-drag-${item.code}`}
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </button>
      </td>
      <td className="px-4 py-2 font-mono-tab font-semibold">{item.code}</td>
      <td className="px-4 py-2">{item.label}</td>
      {showColor && (
        <td className="hidden sm:table-cell px-4 py-2 w-32" data-testid={`master-${testkey}-color-${item.code}`}>
          <span
            className="inline-flex items-center gap-2 text-[11px]"
            style={{ color: swatch.fg }}
          >
            <span
              className="h-3.5 w-3.5 rounded-full border"
              style={{ background: swatch.accent, borderColor: swatch.border }}
              aria-hidden
            />
            <span className="capitalize">{item.color || "—"}</span>
          </span>
        </td>
      )}
      <td className="px-4 py-2">
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => onEdit(item)} className="h-7 w-7 rounded-md hover:bg-[#F3F4F6] flex items-center justify-center" data-testid={`master-${testkey}-edit-${item.code}`}>
            <Pencil className="h-3.5 w-3.5 text-[#0055FF]" />
          </button>
          <button onClick={() => onDelete(item.id)} className="h-7 w-7 rounded-md hover:bg-[#FEE2E2] flex items-center justify-center" data-testid={`master-${testkey}-del-${item.code}`}>
            <Trash2 className="h-3.5 w-3.5 text-[#991B1B]" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function MasterTable({ title, endpoint, testkey }) {
  const isStatus = testkey === "status";
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, code, label, color?}
  const [saving, setSaving] = useState(false);
  // Small activation constraint keeps click-to-edit responsive — a quick tap
  // on the grip won't start a phantom drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const load = async () => {
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const save = async () => {
    if (!editing?.code?.trim() || !editing?.label?.trim()) return;
    setSaving(true);
    try {
      const payload = { code: editing.code.trim(), label: editing.label.trim() };
      // Status master carries an optional palette key. Empty / null clears
      // any custom color and lets the shared helper fall back to slate.
      if (isStatus) payload.color = editing.color || null;
      if (editing.id) {
        await api.patch(`${endpoint}/${editing.id}`, payload);
        toast.success("Item diperbarui");
      } else {
        await api.post(endpoint, payload);
        toast.success("Item ditambahkan");
      }
      // Invalidate the shared /api/reference cache so open pages (Kanban,
      // Tickets, Dashboard, TicketMap) pick up the new color on next render.
      if (isStatus) invalidateStatusReference();
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus item ini?")) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Item dihapus");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((x) => x.id === active.id);
    const newIndex = items.findIndex((x) => x.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const prev = items;
    const next = arrayMove(items, oldIndex, newIndex);
    // Optimistic UI: reorder in place, then persist. Only PATCH rows whose
    // order actually changed to keep the request count minimal.
    setItems(next);
    const changed = next
      .map((it, idx) => ({ id: it.id, code: it.code, order: idx }))
      .filter((row, idx) => prev[idx]?.id !== row.id);
    try {
      await Promise.all(
        changed.map((row) => api.patch(`${endpoint}/${row.id}`, { order: row.order }))
      );
      toast.success("Urutan disimpan");
      // Refresh from server so we pick up the canonical order (and any
      // background changes another admin may have made).
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setItems(prev); // rollback on failure
    }
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-md" data-testid={`master-${testkey}`}>
      <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB]">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF]">Master Data</div>
          <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
        </div>
        <button
          onClick={() => setEditing({ code: "", label: "" })}
          className="h-9 px-3 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-xs font-semibold flex items-center gap-1.5"
          data-testid={`master-${testkey}-add`}
        >
          <Plus className="h-3.5 w-3.5" /> Tambah
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] bg-[#F8F9FA]">
          <tr>
            <th className="pl-3 pr-1 py-2 w-8" aria-label="Urutkan"></th>
            <th className="px-4 py-2 w-32">Kode</th>
            <th className="px-4 py-2">Label</th>
            {isStatus && <th className="hidden sm:table-cell px-4 py-2 w-32">Warna</th>}
            <th className="px-4 py-2 w-24 text-right">Aksi</th>
          </tr>
        </thead>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
            <tbody>
              {items.map((it) => (
                <SortableMasterRow
                  key={it.id}
                  item={it}
                  testkey={testkey}
                  showColor={isStatus}
                  onEdit={(item) => setEditing({ ...item })}
                  onDelete={del}
                />
              ))}
              {items.length === 0 && (
                <tr><td colSpan={isStatus ? 5 : 4} className="px-4 py-6 text-center text-[#9CA3AF] text-xs">Belum ada item.</td></tr>
              )}
            </tbody>
          </SortableContext>
        </DndContext>
      </table>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">
              {editing?.id ? "Edit Item" : "Tambah Item"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Kode</label>
              <input
                value={editing?.code || ""}
                onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                className={inputCls}
                data-testid={`master-${testkey}-input-code`}
                placeholder="Contoh: PSB"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Label</label>
              <input
                value={editing?.label || ""}
                onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                className={inputCls}
                data-testid={`master-${testkey}-input-label`}
                placeholder="Contoh: Pemasangan Baru"
              />
            </div>
            {isStatus && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Warna Status</label>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Pilih warna status" data-testid={`master-${testkey}-color-picker`}>
                  {PALETTE_KEYS.map((k) => {
                    const t = STATUS_PALETTE[k];
                    const selected = editing?.color === k;
                    return (
                      <button
                        key={k}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        aria-label={k}
                        title={k}
                        onClick={() => setEditing({ ...editing, color: k })}
                        className={`h-8 w-8 rounded-full border-2 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0055FF] transition-transform ${selected ? "scale-110" : "hover:scale-105"}`}
                        style={{
                          background: t.accent,
                          borderColor: selected ? "#0A0A0A" : t.border,
                        }}
                        data-testid={`master-${testkey}-color-swatch-${k}`}
                      >
                        {selected && <span className="h-2 w-2 rounded-full bg-white" aria-hidden />}
                      </button>
                    );
                  })}
                </div>
                {/* Preview using the actual pill style so admins see the
                    end result before pressing Simpan. */}
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Pratinjau</span>
                  {(() => {
                    const t = STATUS_PALETTE[editing?.color] || STATUS_PALETTE.slate;
                    return (
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-widest border"
                        style={{ background: t.bg, color: t.fg, borderColor: t.border }}
                        data-testid={`master-${testkey}-color-preview`}
                      >
                        <span className="h-2 w-2 rounded-full" style={{ background: t.accent }} />
                        {editing?.code || "STATUS"}
                      </span>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={save}
              disabled={saving}
              className="h-10 px-5 rounded-md bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-60 text-white font-semibold flex items-center gap-2"
              data-testid={`master-${testkey}-save`}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Simpan
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PRIV_LABELS = {
  dashboard: "Dashboard",
  tickets: "Halaman Tiket",
  kanban: "Status Pekerjaan",
  users: "Manajemen User",
  settings: "Akun & Password",
  app_settings: "Pengaturan Aplikasi",
  ticket_create: "Buat Tiket",
  ticket_edit_any: "Edit Tiket Apapun",
  ticket_delete: "Hapus Tiket",
  excel_import: "Import Excel",
  excel_export: "Ekspor Excel",
  photo_upload: "Unggah Foto",
  photo_delete: "Hapus Foto",
  manage_master: "Kelola Master Data",
  manage_privileges: "Kelola Hak Akses",
  view_reschedule_all: "Lihat Semua Reschedule",
  report_create: "Laporkan Temuan",
  report_review: "Persetujuan Temuan",
  attendance_recap_view: "Lihat Rekap Absensi",
};

function PrivilegesTab() {
  const [privs, setPrivs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/role-privileges").then(({ data }) => setPrivs(data)).catch(() => {});
  }, []);

  const toggle = (role, key) => {
    if (role === "admin") return; // admin always full
    setPrivs((p) => ({ ...p, [role]: { ...p[role], [key]: !p[role][key] } }));
  };

  const saveRole = async (role) => {
    setSaving(true);
    try {
      await api.patch(`/settings/role-privileges/${role}`, { privileges: privs[role] });
      toast.success(`Hak akses ${role} disimpan`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!privs) return <div className="text-sm text-[#9CA3AF]">Memuat...</div>;

  const keys = Object.keys(PRIV_LABELS);
  const roles = ["admin", "koordinator", "teknisi"];

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-md" data-testid="privileges-table">
      <div className="p-4 border-b border-[#E5E7EB] flex items-center gap-2">
        <Shield className="h-4 w-4 text-[#EA580C]" />
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF]">Struktur Dasar</div>
          <h3 className="font-display text-lg font-bold tracking-tight">Manajemen Hak Akses (User Privilege)</h3>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Kelola modul apa saja yang boleh diakses tiap role. Perubahan disimpan per-role.
            <span className="text-[#EA580C] font-semibold"> Enforcement penuh akan diaktifkan bertahap.</span>
          </p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] bg-[#F8F9FA]">
            <tr>
              <th className="px-4 py-3">Modul / Aksi</th>
              {roles.map((r) => (
                <th key={r} className="px-4 py-3 text-center">{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k} className="border-t border-[#E5E7EB]">
                <td className="px-4 py-2 font-medium">{PRIV_LABELS[k]}</td>
                {roles.map((r) => (
                  <td key={r} className="px-4 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!privs[r]?.[k]}
                      onChange={() => toggle(r, k)}
                      disabled={r === "admin"}
                      className="h-4 w-4 accent-[#0055FF] disabled:opacity-50"
                      data-testid={`priv-${r}-${k}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-4 border-t border-[#E5E7EB] flex justify-end gap-2">
        {["koordinator", "teknisi"].map((r) => (
          <button
            key={r}
            onClick={() => saveRole(r)}
            disabled={saving}
            className="h-9 px-3 rounded-md bg-[#0A0A0A] hover:bg-[#333] disabled:opacity-60 text-white text-xs font-semibold flex items-center gap-2"
            data-testid={`priv-save-${r}`}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Simpan {r}
          </button>
        ))}
      </div>
    </div>
  );
}

const ACTION_LABEL = {
  login: { label: "Login", bg: "#D1FAE5", fg: "#065F46" },
  login_failed: { label: "Login Gagal", bg: "#FEE2E2", fg: "#991B1B" },
  logout: { label: "Logout", bg: "#F3F4F6", fg: "#4B5563" },
  session_idle: { label: "Sesi Idle", bg: "#FEF3C7", fg: "#92400E" },
  session_expired: { label: "Sesi Expired", bg: "#FEF3C7", fg: "#92400E" },
  session_revoked: { label: "Sesi Dicabut", bg: "#FEE2E2", fg: "#991B1B" },
  user_created: { label: "User Dibuat", bg: "#D1FAE5", fg: "#065F46" },
  user_deleted: { label: "User Dihapus", bg: "#FEE2E2", fg: "#991B1B" },
  password_reset: { label: "Reset Password", bg: "#FEF3C7", fg: "#92400E" },
  role_changed: { label: "Role Diubah", bg: "#DBEAFE", fg: "#1E40AF" },
  ticket_created: { label: "Tiket Dibuat", bg: "#D1FAE5", fg: "#065F46" },
  ticket_updated: { label: "Tiket Diubah", bg: "#DBEAFE", fg: "#1E40AF" },
  ticket_assigned: { label: "Tiket Assign", bg: "#EDE9FE", fg: "#5B21B6" },
  ticket_claimed: { label: "Tiket Klaim", bg: "#EDE9FE", fg: "#5B21B6" },
  ticket_status_changed: { label: "Status Diubah", bg: "#DBEAFE", fg: "#1E40AF" },
  ticket_deleted: { label: "Tiket Dihapus", bg: "#FEE2E2", fg: "#991B1B" },
  ticket_photo_uploaded: { label: "Foto Diunggah", bg: "#D1FAE5", fg: "#065F46" },
  ticket_photo_deleted: { label: "Foto Dihapus", bg: "#FEE2E2", fg: "#991B1B" },
  excel_imported: { label: "Excel Diimpor", bg: "#DBEAFE", fg: "#1E40AF" },
  excel_exported: { label: "Excel Diekspor", bg: "#DBEAFE", fg: "#1E40AF" },
  created: { label: "Dibuat", bg: "#D1FAE5", fg: "#065F46" },
  updated: { label: "Diubah", bg: "#DBEAFE", fg: "#1E40AF" },
  deleted: { label: "Dihapus", bg: "#FEE2E2", fg: "#991B1B" },
};

const CATEGORY_OPTIONS = [
  { value: "", label: "Semua Kategori" },
  { value: "auth", label: "Autentikasi" },
  { value: "user", label: "User" },
  { value: "permission", label: "Hak Akses" },
  { value: "ticket", label: "Tiket" },
  { value: "master", label: "Master Data" },
  { value: "system", label: "Sistem" },
  // Fase 5.6A — shortcut ke audit event `attendance_flag_changed` (Fase
  // 5.4). Value sentinel dengan prefix `action:` supaya `load()` men-route
  // ke param `action` di backend, bukan `category`. Backend endpoint
  // `GET /settings/audit-log` sudah support both — tidak perlu perubahan
  // backend.
  { value: "action:attendance_flag_changed", label: "Perubahan Status Absensi" },
];

function AuditLogTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [category, setCategory] = useState("");
  const [username, setUsername] = useState("");
  const [role, setRole] = useState("");
  const [datePreset, setDatePreset] = useState("7"); // 7 | 30 | custom
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const _computeDateRange = () => {
    if (datePreset === "custom") return { date_from: dateFrom || undefined, date_to: dateTo || undefined };
    const days = parseInt(datePreset, 10);
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    const iso = (d) => d.toISOString().slice(0, 10);
    return { date_from: iso(from), date_to: iso(to) };
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      // Fase 5.6A — sentinel value `action:<name>` route ke param backend
      // `action` untuk exact match. Kategori biasa tetap ke `category`.
      if (category.startsWith("action:")) {
        params.action = category.slice("action:".length);
      } else if (category) {
        params.category = category;
      }
      if (username) params.username = username.trim();
      if (role) params.role = role;
      if (q) params.q = q.trim();
      const range = _computeDateRange();
      if (range.date_from) params.date_from = range.date_from;
      if (range.date_to) params.date_to = range.date_to;
      const { data } = await api.get("/settings/audit-log", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [category, role, datePreset, dateFrom, dateTo]);

  const CATEGORY_LABEL = {
    // by `category` (new schema)
    auth: "Autentikasi",
    user: "User",
    permission: "Hak Akses",
    ticket: "Tiket",
    master: "Master Data",
    system: "Sistem",
    // by legacy `collection` (fallback)
    role_privileges: "Hak Akses",
    session_policy: "Kebijakan Sesi",
    master_sumber: "Sumber Tiket",
    master_jenis: "Jenis Pekerjaan",
    master_prioritas: "Prioritas",
    master_status: "Status Tiket",
    master_wilayah: "Wilayah",
    users: "User",
    tickets: "Tiket",
    ticket_photos: "Foto Tiket",
    excel_import: "Impor Excel",
    excel_export: "Ekspor Excel",
  };

  const badge = (a) => {
    const c = ACTION_LABEL[a] || { label: a, bg: "#F3F4F6", fg: "#4B5563" };
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
            style={{ background: c.bg, color: c.fg }}>
        {c.label}
      </span>
    );
  };

  const renderPrivDiff = (before, after) => {
    if (!before || !after) return null;
    const roleName = Object.keys(after)[0];
    const b = (before[roleName] || {});
    const a = (after[roleName] || {});
    const changed = Object.keys(a).filter((k) => a[k] !== b[k]);
    if (!changed.length) return <span className="text-[#9CA3AF] italic">Tidak ada perubahan efektif</span>;
    return (
      <div className="space-y-0.5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">
          Role: <span className="text-[#0A0A0A]">{roleName}</span>
        </div>
        {changed.map((flag) => (
          <div key={flag} className="font-mono-tab text-[11px]">
            <span className="font-semibold text-[#0A0A0A]">{flag}</span>:{" "}
            <span className="text-[#991B1B]">{String(b[flag] ?? "—")}</span>
            <span className="mx-1 text-[#9CA3AF]">→</span>
            <span className="text-[#065F46] font-semibold">{String(a[flag])}</span>
          </div>
        ))}
      </div>
    );
  };

  // Compact one-line summary — full detail goes into the dialog.
  const summary = (r) => {
    if (r.description) return r.description;
    if (r.collection === "role_privileges") return renderPrivDiff(r.before, r.after);
    if (r.no_tiket) return `Tiket ${r.no_tiket}`;
    return "—";
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-md" data-testid="audit-log-table">
      <div className="p-4 border-b border-[#E5E7EB]">
        <div className="flex items-start gap-3 mb-4">
          <ScrollText className="h-4 w-4 text-[#0055FF] flex-shrink-0 mt-1" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF]">Riwayat</div>
            <h3 className="font-display text-lg font-bold tracking-tight">Aktivitas Sistem</h3>
            <p className="text-xs text-[#9CA3AF]">Setiap aksi penting user tercatat di sini untuk monitoring, keamanan, dan investigasi.</p>
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-6 lg:gap-2">
          <select className={inputCls + " h-9 text-xs"} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="audit-filter-category">
            {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className={inputCls + " h-9 text-xs"} value={role} onChange={(e) => setRole(e.target.value)} data-testid="audit-filter-role">
            <option value="">Semua Role</option>
            <option value="admin">Admin</option>
            <option value="koordinator">Koordinator</option>
            <option value="teknisi">Teknisi</option>
          </select>
          <input className={inputCls + " h-9 text-xs"} placeholder="Username…" value={username}
                 onChange={(e) => setUsername(e.target.value)} data-testid="audit-filter-username"
                 onKeyDown={(e) => e.key === "Enter" && load()} />
          <input className={inputCls + " h-9 text-xs"} placeholder="No. tiket / cari…" value={q}
                 onChange={(e) => setQ(e.target.value)} data-testid="audit-filter-q"
                 onKeyDown={(e) => e.key === "Enter" && load()} />
          <select className={inputCls + " h-9 text-xs"} value={datePreset}
                  onChange={(e) => setDatePreset(e.target.value)} data-testid="audit-filter-date-preset">
            <option value="7">7 Hari Terakhir</option>
            <option value="30">30 Hari Terakhir</option>
            <option value="custom">Custom…</option>
          </select>
          <button
            onClick={load}
            className="h-9 px-3 rounded-md bg-[#0A0A0A] hover:bg-[#333] text-white text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            disabled={loading}
            data-testid="audit-filter-apply"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Terapkan"}
          </button>
        </div>
        {datePreset === "custom" && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <input type="date" className={inputCls + " h-9 text-xs"} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="audit-filter-date-from" />
            <input type="date" className={inputCls + " h-9 text-xs"} value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="audit-filter-date-to" />
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] bg-[#F8F9FA]">
            <tr>
              <th className="px-4 py-3">Waktu</th>
              <th className="px-4 py-3">Oleh</th>
              <th className="px-4 py-3">Aksi</th>
              <th className="px-4 py-3">Kategori</th>
              <th className="px-4 py-3">Ringkasan</th>
              <th className="px-4 py-3 text-right">Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#E5E7EB] align-top hover:bg-[#F8F9FA]" data-testid={`audit-row-${r.id}`}>
                <td className="px-4 py-2 text-[11px] font-mono-tab text-[#4B5563] whitespace-nowrap">{formatWIB(r.at)}</td>
                <td className="px-4 py-2">
                  <div className="text-xs font-semibold text-[#0A0A0A]">{r.by_name}</div>
                  <div className="text-[10px] text-[#9CA3AF] font-mono-tab">
                    @{r.by}{r.role_of_user ? ` · ${r.role_of_user}` : ""}
                  </div>
                </td>
                <td className="px-4 py-2">{badge(r.action)}</td>
                <td className="px-4 py-2 text-xs">
                  <div className="font-semibold text-[#0A0A0A]">{CATEGORY_LABEL[r.category || r.collection] || r.category || r.collection}</div>
                </td>
                <td className="px-4 py-2 text-xs max-w-md">{summary(r)}</td>
                <td className="px-4 py-2 text-right">
                  <button className="text-[11px] font-semibold text-[#0055FF] hover:underline"
                          onClick={() => setDetail(r)} data-testid={`audit-detail-${r.id}`}>Lihat</button>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-[#9CA3AF] text-xs">Tidak ada aktivitas pada rentang & filter ini.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">Detail Aktivitas</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-[#9CA3AF]">Waktu:</span> <span className="font-mono-tab">{formatWIB(detail.at)}</span></div>
                <div><span className="text-[#9CA3AF]">Oleh:</span> {detail.by_name} <span className="text-[#9CA3AF]">(@{detail.by})</span></div>
                <div><span className="text-[#9CA3AF]">Role:</span> {detail.role_of_user || "—"}</div>
                <div><span className="text-[#9CA3AF]">Aksi:</span> {badge(detail.action)}</div>
                {detail.no_tiket && <div><span className="text-[#9CA3AF]">No. Tiket:</span> <span className="font-mono-tab">{detail.no_tiket}</span></div>}
                {detail.target_id && <div><span className="text-[#9CA3AF]">Target ID:</span> <span className="font-mono-tab break-all text-[10px]">{detail.target_id}</span></div>}
                {detail.ip && <div><span className="text-[#9CA3AF]">IP:</span> <span className="font-mono-tab">{detail.ip}</span></div>}
                {detail.ua && <div className="col-span-2"><span className="text-[#9CA3AF]">User-Agent:</span> <span className="font-mono-tab text-[10px] break-all">{detail.ua}</span></div>}
              </div>
              {detail.description && <p className="text-sm">{detail.description}</p>}
              {(detail.before || detail.after || detail.metadata) && (
                <div className="space-y-1 border-t pt-3">
                  {detail.before && (
                    <div className="text-[11px]"><b className="text-[#991B1B]">Before:</b> <span className="font-mono-tab break-all">{JSON.stringify(detail.before)}</span></div>
                  )}
                  {detail.after && (
                    <div className="text-[11px]"><b className="text-[#065F46]">After:</b> <span className="font-mono-tab break-all">{JSON.stringify(detail.after)}</span></div>
                  )}
                  {detail.metadata && (
                    <div className="text-[11px]"><b className="text-[#4B5563]">Metadata:</b> <span className="font-mono-tab break-all">{JSON.stringify(detail.metadata)}</span></div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SecurityTab() {
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/session-policy");
      setPolicy(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!policy) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/session-policy", {
        idle_timeout_hours: policy.idle_timeout_hours,
        absolute_timeout_days: policy.absolute_timeout_days,
      });
      setPolicy((p) => ({ ...p, ...data }));
      toast.success("Kebijakan sesi disimpan");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!policy) return <div className="text-sm text-[#9CA3AF]">Memuat...</div>;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-md" data-testid="security-session-policy">
      <div className="p-4 border-b border-[#E5E7EB] flex items-center gap-2">
        <Lock className="h-4 w-4 text-[#0055FF]" />
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF]">Keamanan</div>
          <h3 className="font-display text-lg font-bold tracking-tight">Sesi Login</h3>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Atur berapa lama sesi tidak-aktif bertahan (idle timeout) dan berapa lama sesi maksimum sebelum wajib login ulang (absolute timeout). Berlaku untuk semua user.
          </p>
        </div>
      </div>

      <div className="p-6 grid gap-6 sm:grid-cols-2">
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-widest text-[#4B5563] mb-2">
            Idle timeout
          </label>
          <select
            className={inputCls}
            value={policy.idle_timeout_hours}
            onChange={(e) => setPolicy({ ...policy, idle_timeout_hours: Number(e.target.value) })}
            data-testid="security-idle-select"
          >
            {(policy.idle_choices || [1, 4, 8, 12, 24]).map((h) => (
              <option key={h} value={h}>{h === 24 ? "24 jam" : `${h} jam`}</option>
            ))}
          </select>
          <p className="text-[11px] text-[#9CA3AF] mt-1.5">
            Sesi berakhir jika tidak ada aktivitas selama durasi ini. Peringatan muncul 5 menit sebelum berakhir.
          </p>
        </div>

        <div>
          <label className="block text-[11px] font-bold uppercase tracking-widest text-[#4B5563] mb-2">
            Maksimum masa sesi
          </label>
          <select
            className={inputCls}
            value={policy.absolute_timeout_days}
            onChange={(e) => setPolicy({ ...policy, absolute_timeout_days: Number(e.target.value) })}
            data-testid="security-abs-select"
          >
            {(policy.absolute_choices || [1, 3, 7, 14, 30]).map((d) => (
              <option key={d} value={d}>{d === 1 ? "1 hari" : `${d} hari`}</option>
            ))}
          </select>
          <p className="text-[11px] text-[#9CA3AF] mt-1.5">
            Sesi wajib login ulang setelah durasi ini, walaupun user tetap aktif.
          </p>
        </div>
      </div>

      <div className="p-4 border-t border-[#E5E7EB] flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="h-9 px-4 rounded-md bg-[#0A0A0A] hover:bg-[#333] disabled:opacity-60 text-white text-xs font-semibold flex items-center gap-2"
          data-testid="security-save"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Simpan Kebijakan Sesi
        </button>
      </div>
    </div>
  );
}


// Sep 2026 Phase 22q — Invoice Branding: logo PNG + slogan editable.
// Konfigurasi disimpan di `settings` collection (key: invoice_branding)
// via endpoint /api/settings/invoice-branding. Tidak menyentuh business
// logic Billing V1.
function InvoiceBrandingTab() {
  const [logo, setLogo] = useState("");
  const [slogan, setSlogan] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyNpwp, setCompanyNpwp] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAcct, setBankAcct] = useState("");
  const [bankHolder, setBankHolder] = useState("");
  const [qris, setQris] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/invoice-branding");
      setLogo(data.logo_data_url || "");
      setSlogan(data.slogan || "");
      setCompanyName(data.company_name || "");
      setCompanyAddress(data.company_address || "");
      setCompanyNpwp(data.company_npwp || "");
      setCompanyPhone(data.company_phone || "");
      setBankName(data.bank_name || "");
      setBankAcct(data.bank_account_number || "");
      setBankHolder(data.bank_account_holder || "");
      setQris(data.qris_data_url || "");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "image/png") {
      toast.error("File harus PNG");
      e.target.value = "";
      return;
    }
    if (f.size > 300 * 1024) {
      toast.error("Logo maksimal ~300KB");
      e.target.value = "";
      return;
    }
    const r = new FileReader();
    r.onload = () => setLogo(String(r.result || ""));
    r.readAsDataURL(f);
  };

  const onQrisFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "image/png") {
      toast.error("File QRIS harus PNG");
      e.target.value = "";
      return;
    }
    if (f.size > 300 * 1024) {
      toast.error("QRIS maksimal ~300KB");
      e.target.value = "";
      return;
    }
    const r = new FileReader();
    r.onload = () => setQris(String(r.result || ""));
    r.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        logo_data_url: logo,
        slogan,
        company_name: companyName,
        company_address: companyAddress,
        company_npwp: companyNpwp,
        company_phone: companyPhone,
        bank_name: bankName,
        bank_account_number: bankAcct,
        bank_account_holder: bankHolder,
        qris_data_url: qris,
      };
      const { data } = await api.patch("/settings/invoice-branding", body);
      setLogo(data.logo_data_url || "");
      setSlogan(data.slogan || "");
      setCompanyName(data.company_name || "");
      setCompanyAddress(data.company_address || "");
      setCompanyNpwp(data.company_npwp || "");
      setCompanyPhone(data.company_phone || "");
      setBankName(data.bank_name || "");
      setBankAcct(data.bank_account_number || "");
      setBankHolder(data.bank_account_holder || "");
      setQris(data.qris_data_url || "");
      toast.success("Identitas invoice tersimpan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm">Memuat…</div>;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="invoice-branding-tab">
      <div>
        <h3 className="text-lg font-bold flex items-center gap-2">
          <ImageIcon className="w-5 h-5" /> Identitas Invoice
        </h3>
        <p className="text-sm text-[#6B7280] mt-1">
          Logo, slogan, & info perusahaan yang muncul di header invoice. Perubahan langsung dipakai pada invoice berikutnya.
        </p>
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-semibold">Logo Invoice (PNG)</label>
        <input type="file" accept="image/png" onChange={onFile}
               className="block text-sm" data-testid="branding-logo-input" />
        {logo && (
          <div className="flex items-center gap-3 mt-2 p-2 border rounded bg-white">
            <img src={logo} alt="Preview logo" className="h-14 w-auto max-w-[180px] object-contain"
                 data-testid="branding-logo-preview" />
            <Button size="sm" variant="outline" onClick={() => setLogo("")}
                    data-testid="branding-logo-remove">
              <Trash2 className="w-3 h-3 mr-1" /> Hapus
            </Button>
          </div>
        )}
        <div className="text-[11px] text-[#6B7280]">Format PNG, background transparan didukung, maks ~300KB.</div>
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-semibold">Nama Perusahaan</label>
        <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)}
               placeholder="AROMA NET" maxLength={120}
               data-testid="branding-company-name-input" />
        <div className="text-[11px] text-[#6B7280]">Tampil di bawah logo (header kiri) invoice.</div>
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-semibold">Slogan / Tagline</label>
        <Input value={slogan} onChange={(e) => setSlogan(e.target.value)}
               placeholder="Internet Cepat & Terpercaya" maxLength={200}
               data-testid="branding-slogan-input" />
        <div className="text-[11px] text-[#6B7280]">Kosongkan bila tidak ingin menampilkan slogan.</div>
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-semibold">Alamat Perusahaan</label>
        <Textarea value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)}
                  placeholder="Desa Batur RT 03 RW 02, Kec. Batur, Kab. Banjarnegara, Jawa Tengah 53456"
                  maxLength={500} rows={3}
                  data-testid="branding-company-address-input" />
        <div className="text-[11px] text-[#6B7280]">Tampil di header kanan invoice. Baris baru didukung.</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-semibold">No. NPWP</label>
          <Input value={companyNpwp} onChange={(e) => setCompanyNpwp(e.target.value)}
                 placeholder="12.345.678.9-123.000" maxLength={40}
                 data-testid="branding-company-npwp-input" />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-semibold">No. Telp / WhatsApp</label>
          <Input value={companyPhone} onChange={(e) => setCompanyPhone(e.target.value)}
                 placeholder="0812-3456-7890" maxLength={40}
                 data-testid="branding-company-phone-input" />
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold">Preview Header Invoice</label>
        <div className="flex items-start justify-between gap-4 p-3 border rounded bg-[#F8F9FA]"
             data-testid="branding-preview">
          <div className="flex items-start gap-3 min-w-0">
            {logo && <img src={logo} alt="Preview" className="h-12 w-auto max-w-[120px] object-contain" />}
            <div className="min-w-0">
              <div className="text-base font-black tracking-tight truncate">{companyName || "Nama Perusahaan"}</div>
              {slogan && <div className="text-xs italic text-[#374151] mt-0.5">{slogan}</div>}
            </div>
          </div>
          <div className="text-[11px] text-[#374151] text-right whitespace-pre-line max-w-[45%]">
            {companyAddress && <div><span className="text-[#6B7280]">Alamat: </span>{companyAddress}</div>}
            {companyNpwp && <div><span className="text-[#6B7280]">NPWP: </span>{companyNpwp}</div>}
            {companyPhone && <div><span className="text-[#6B7280]">Telp/WA: </span>{companyPhone}</div>}
          </div>
        </div>
      </div>

      <div className="pt-3 border-t">
        <h4 className="text-sm font-bold mb-2">Info Pembayaran (opsional)</h4>
        <p className="text-xs text-[#6B7280] mb-3">Ditampilkan di callout "Informasi Pembayaran" pada invoice PDF. Kosongkan jika tidak ingin ditampilkan.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold">Nama Bank</label>
            <Input value={bankName} onChange={(e) => setBankName(e.target.value)}
                   placeholder="BRI" maxLength={80}
                   data-testid="branding-bank-name-input" />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-semibold">No. Rekening</label>
            <Input value={bankAcct} onChange={(e) => setBankAcct(e.target.value)}
                   placeholder="1234-5678-9012" maxLength={40}
                   data-testid="branding-bank-acct-input" />
          </div>
        </div>

        <div className="space-y-1.5 mt-3">
          <label className="text-sm font-semibold">Atas Nama</label>
          <Input value={bankHolder} onChange={(e) => setBankHolder(e.target.value)}
                 placeholder="AROMA NET MEDIA" maxLength={120}
                 data-testid="branding-bank-holder-input" />
        </div>

        <div className="space-y-1.5 mt-3">
          <label className="text-sm font-semibold">QRIS (PNG)</label>
          <input type="file" accept="image/png" onChange={onQrisFile}
                 className="block text-sm" data-testid="branding-qris-input" />
          {qris && (
            <div className="flex items-center gap-3 mt-2 p-2 border rounded bg-white">
              <img src={qris} alt="QRIS" className="h-20 w-20 object-contain"
                   data-testid="branding-qris-preview" />
              <Button size="sm" variant="outline" onClick={() => setQris("")}
                      data-testid="branding-qris-remove">
                <Trash2 className="w-3 h-3 mr-1" /> Hapus QRIS
              </Button>
            </div>
          )}
          <div className="text-[11px] text-[#6B7280]">Format PNG (transparan didukung), maks ~300KB.</div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="branding-save-btn">
        <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Menyimpan…" : "Simpan"}
      </Button>
    </div>
  );
}


export default function AppSettings() {
  return (
    <AppSettingsInner />
  );
}

// Feb 2026 — Konfigurasi Auto-Isolir + tombol run manual.
function AutoIsolirTab() {
  const [config, setConfig] = useState({ enabled: false, threshold_days: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/auto-isolir/config");
      setConfig({ enabled: !!data.enabled, threshold_days: Number(data.threshold_days || 0) });
    } catch { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/billing/auto-isolir/config", {
        enabled: !!config.enabled,
        threshold_days: Number(config.threshold_days || 0),
      });
      setConfig({ enabled: !!data.enabled, threshold_days: Number(data.threshold_days || 0) });
      toast.success("Konfigurasi Auto-Isolir disimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal simpan");
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/billing/auto-isolir/run");
      setLastRun(data);
      if (data.ran) {
        toast.success(`Auto-Isolir: ${data.isolir_count} customer di-isolir (dari ${data.candidates} kandidat).`);
      } else {
        toast.info("Auto-Isolir tidak dijalankan (fitur nonaktif atau threshold 0).");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menjalankan");
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="auto-isolir-tab">
      <div className="border rounded-lg p-4 space-y-3">
        <h3 className="font-bold flex items-center gap-2"><Lock className="w-5 h-5" /> Auto-Isolir Otomatis</h3>
        <p className="text-xs text-[#6B7280]">
          Menjalankan job harian yang mengubah status customer <b>AKTIF → ISOLIR</b> bila
          memiliki invoice outstanding yang <b>lewat jatuh tempo ≥ N hari</b>. Tidak
          menyentuh invoice/payment; hanya set status. Aman diulang (idempotent).
        </p>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="auto-isolir-enabled"
                 checked={config.enabled}
                 onChange={(e) => setConfig((c) => ({ ...c, enabled: e.target.checked }))}
                 className="w-4 h-4 accent-emerald-700"
                 data-testid="auto-isolir-enabled" />
          <Label htmlFor="auto-isolir-enabled" className="cursor-pointer">Aktifkan job harian</Label>
        </div>
        <div>
          <Label>Threshold (hari lewat jatuh tempo)</Label>
          <Input type="number" min={0} max={365}
                 value={config.threshold_days}
                 onChange={(e) => setConfig((c) => ({ ...c, threshold_days: Number(e.target.value || 0) }))}
                 disabled={saving}
                 className="max-w-[200px]"
                 data-testid="auto-isolir-threshold" />
          <div className="text-[11px] text-[#6B7280] mt-1">0 = nonaktif. Rekomendasi 15-30 hari.</div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button onClick={save} disabled={saving || loading} data-testid="auto-isolir-save">
            {saving ? "Menyimpan…" : "Simpan Konfigurasi"}
          </Button>
          <Button variant="outline" onClick={runNow}
                  disabled={running || !config.enabled || !config.threshold_days}
                  data-testid="auto-isolir-run-now">
            {running ? "Menjalankan…" : "Jalankan Sekarang"}
          </Button>
        </div>
        {lastRun && (
          <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2" data-testid="auto-isolir-last-result">
            Hasil terakhir: <b>{lastRun.isolir_count}</b> customer di-isolir · Kandidat: {lastRun.candidates || 0} · Threshold: {lastRun.threshold_days} hari.
          </div>
        )}
      </div>
    </div>
  );
}

function AppSettingsInner() {
  return (
    <div className="space-y-6" data-testid="app-settings-page">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#9CA3AF]">Administrator</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1">Pengaturan Aplikasi</h1>
        <p className="text-sm text-[#4B5563] mt-2">
          Kelola daftar master dropdown & hak akses per-role. Perubahan langsung diterapkan pada formulir Tiket.
        </p>
      </div>

      <Tabs defaultValue="sumber" className="w-full">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="sumber" data-testid="tab-sumber">
            <Database className="h-3.5 w-3.5 mr-1.5" /> Sumber
          </TabsTrigger>
          <TabsTrigger value="jenis" data-testid="tab-jenis">
            <Database className="h-3.5 w-3.5 mr-1.5" /> Jenis Pekerjaan
          </TabsTrigger>
          <TabsTrigger value="prioritas" data-testid="tab-prioritas">
            <Database className="h-3.5 w-3.5 mr-1.5" /> Prioritas
          </TabsTrigger>
          <TabsTrigger value="status" data-testid="tab-status">
            <Database className="h-3.5 w-3.5 mr-1.5" /> Status
          </TabsTrigger>
          <TabsTrigger value="priv" data-testid="tab-priv">
            <Shield className="h-3.5 w-3.5 mr-1.5" /> Hak Akses
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">
            <Lock className="h-3.5 w-3.5 mr-1.5" /> Keamanan
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">
            <Shield className="h-3.5 w-3.5 mr-1.5" /> Audit Log
          </TabsTrigger>
          <TabsTrigger value="attendance-locs" data-testid="tab-attendance-locs">
            <Shield className="h-3.5 w-3.5 mr-1.5" /> Lokasi Absensi
          </TabsTrigger>
          <TabsTrigger value="invoice-branding" data-testid="tab-invoice-branding">
            <ImageIcon className="h-3.5 w-3.5 mr-1.5" /> Identitas Invoice
          </TabsTrigger>
          <TabsTrigger value="auto-isolir" data-testid="tab-auto-isolir">
            <Lock className="h-3.5 w-3.5 mr-1.5" /> Auto-Isolir
          </TabsTrigger>
        </TabsList>
        <TabsContent value="sumber">
          <MasterTable title="Sumber Tiket" endpoint="/settings/sumber" testkey="sumber" />
        </TabsContent>
        <TabsContent value="jenis">
          <MasterTable title="Jenis Pekerjaan" endpoint="/settings/jenis" testkey="jenis" />
        </TabsContent>
        <TabsContent value="prioritas">
          <MasterTable title="Prioritas (P1-P4 dst)" endpoint="/settings/prioritas" testkey="prioritas" />
        </TabsContent>
        <TabsContent value="status">
          <MasterTable title="Status Tiket" endpoint="/settings/status" testkey="status" />
        </TabsContent>
        <TabsContent value="priv">
          <PrivilegesTab />
        </TabsContent>
        <TabsContent value="security">
          <SecurityTab />
        </TabsContent>
        <TabsContent value="audit">
          <AuditLogTab />
        </TabsContent>
        <TabsContent value="attendance-locs">
          <AttendanceLocationsTab />
        </TabsContent>
        <TabsContent value="invoice-branding">
          <InvoiceBrandingTab />
        </TabsContent>
        <TabsContent value="auto-isolir">
          <AutoIsolirTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AttendanceLocationsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // location doc or new
  const [saving, setSaving] = useState(false);
  // Fase — Late Threshold Global Toggle. Legacy single-value `late_hour`
  // di app_config masih dipertahankan di BACKEND sebagai fallback bila
  // `late_schedule` belum di-set (mis. deployment lama). UI ini tidak
  // lagi mengedit `late_hour` — cukup 7-hari schedule di atas.
  const [history, setHistory] = useState([]);
  // Fase — Late Schedule Per Weekday. Schedule 7 hari (WIB, HH:mm) yang
  // menggantikan single-value `late_hour` sebagai policy utama. Legacy
  // `late_hour` tetap dipertahankan sebagai fallback backend.
  const DEFAULT_SCHEDULE = {
    monday: "10:15", tuesday: "10:15", wednesday: "10:15", thursday: "10:15",
    friday: "14:00", saturday: "10:15", sunday: "10:15",
  };
  const DAY_LABEL = { monday: "Senin", tuesday: "Selasa", wednesday: "Rabu",
                      thursday: "Kamis", friday: "Jumat", saturday: "Sabtu",
                      sunday: "Minggu" };
  const DAY_ORDER = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [savingSchedule, setSavingSchedule] = useState(false);
  // Fase — Per-Location Audit Log. Riwayat perubahan `late_hour` untuk
  // lokasi yang sedang di-edit. Di-fetch dari endpoint dedicated
  // `/attendance/locations/{id}/history` (admin+koord). Dikosongkan
  // saat dialog ditutup atau saat membuat lokasi baru (tidak ada id).
  const [locHistory, setLocHistory] = useState([]);
  const [locHistoryLoading, setLocHistoryLoading] = useState(false);

  const empty = { name: "", code: "", description: "", latitude: -7.205848, longitude: 109.827371, radius_meter: 100, accuracy_threshold_meter: 50, late_hour: 8, active: true };

  const load = async () => {
    setLoading(true);
    try {
      const [locsResp, settingsResp, historyResp] = await Promise.all([
        api.get("/attendance/locations"),
        api.get("/settings/attendance"),
        api.get("/settings/attendance/history").catch(() => ({ data: { entries: [] } })),
      ]);
      setRows(locsResp.data || []);
      if (settingsResp.data?.late_schedule && typeof settingsResp.data.late_schedule === "object") {
        setSchedule({ ...DEFAULT_SCHEDULE, ...settingsResp.data.late_schedule });
      }
      setHistory(historyResp.data?.entries || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Fase — Per-Location Audit Log. Fetch riwayat perubahan `late_hour`
  // untuk lokasi yang sedang di-edit. Silent fallback bila endpoint
  // menolak (403 non-admin/koord) — riwayat tidak wajib untuk save.
  useEffect(() => {
    if (!editing?.id) { setLocHistory([]); return; }
    let cancelled = false;
    (async () => {
      setLocHistoryLoading(true);
      try {
        const { data } = await api.get(`/attendance/locations/${editing.id}/history`);
        if (!cancelled) setLocHistory(data?.entries || []);
      } catch {
        if (!cancelled) setLocHistory([]);
      } finally {
        if (!cancelled) setLocHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editing?.id]);

  const saveGlobalLateHour = async () => {
    // Legacy no-op. Non-invoked. Kept only to satisfy tools that may
    // reference the symbol name; UI kini memakai `saveSchedule` untuk
    // menyimpan 7-hari schedule.
    return;
  };
  void saveGlobalLateHour;

  // Fase — Late Schedule Per Weekday. Validasi client-side HH:mm 24-jam
  // sebelum PUT; backend validate ulang (server-side source of truth).
  const saveSchedule = async () => {
    const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
    const invalid = DAY_ORDER.filter((d) => !HHMM.test((schedule[d] || "").trim()));
    if (invalid.length > 0) {
      toast.error(`Format tidak valid untuk: ${invalid.map((d) => DAY_LABEL[d]).join(", ")}. Gunakan HH:mm (24-jam)`);
      return;
    }
    setSavingSchedule(true);
    try {
      await api.put("/settings/attendance", { late_schedule: schedule });
      toast.success("Jadwal toleransi terlambat tersimpan");
      const h = await api.get("/settings/attendance/history").catch(() => ({ data: { entries: [] } }));
      setHistory(h.data?.entries || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingSchedule(false);
    }
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const body = {
        name: editing.name.trim(),
        code: editing.code.trim(),
        description: (editing.description || "").trim(),
        latitude: Number(editing.latitude),
        longitude: Number(editing.longitude),
        radius_meter: Number(editing.radius_meter) || 100,
        accuracy_threshold_meter: Number(editing.accuracy_threshold_meter) || 50,
        late_hour: Number.isFinite(Number(editing.late_hour)) ? Number(editing.late_hour) : 8,
        active: !!editing.active,
      };
      if (editing.id) {
        await api.patch(`/attendance/locations/${editing.id}`, body);
      } else {
        await api.post("/attendance/locations", body);
      }
      toast.success("Lokasi disimpan");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus lokasi ini? User yang memakainya akan kehilangan akses lokasi tersebut.")) return;
    try {
      await api.delete(`/attendance/locations/${id}`);
      toast.success("Lokasi dihapus");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const inputCls = "w-full h-10 px-3 rounded-md border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#0055FF] text-sm";

  return (
    <div className="space-y-4" data-testid="attendance-locs-tab">
      {/* Fase — Late Schedule Per Weekday. Card di paling atas menegaskan
          bahwa policy default absensi ada di sini. Legacy `late_hour`
          single-value TIDAK ditampilkan lagi (masih di backend sebagai
          fallback) supaya UI tetap sederhana — admin cukup fokus pada
          7-hari schedule. Field `late_hour` per-lokasi tetap ada di
          dialog edit lokasi masing-masing. */}
      <div className="bg-white border border-[#E5E7EB] rounded-md p-4" data-testid="global-late-schedule-card">
        <div className="mb-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#4B5563]">Global Default</div>
          <div className="text-sm font-semibold text-[#0A0A0A]">Pengaturan Toleransi Terlambat</div>
          <p className="text-[11px] text-[#6B7280] mt-1 leading-relaxed">
            Check-in <b>sebelum</b> batas waktu dianggap <b>TEPAT WAKTU (HADIR)</b>. Check-in <b>pada atau setelah</b> batas waktu dianggap <b>TERLAMBAT</b>. Semua waktu menggunakan WIB (format 24 jam / HH:mm).
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
          {DAY_ORDER.map((d) => {
            const val = schedule[d] || "";
            const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
            const isValid = HHMM.test(val);
            // Fase — Fix 24-jam. Native `input type="time"` menampilkan
            // AM/PM pada browser locale English. Gunakan text input dengan
            // masking numeric-only + auto-insert ":" saat panjang == 2
            // supaya tampilan SELALU 24-jam HH:mm terlepas dari locale.
            // Backend contract tidak berubah (validasi HH:mm regex).
            const onChange = (e) => {
              // Ambil hanya digit; sisipkan ":" setelah 2 digit. Max 4
              // digit → format HH:mm (5 chars total).
              const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
              let masked = digits;
              if (digits.length >= 3) masked = `${digits.slice(0, 2)}:${digits.slice(2)}`;
              setSchedule({ ...schedule, [d]: masked });
            };
            return (
              <label key={d} className="flex items-center justify-between gap-2 rounded-md border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2">
                <span className="text-[11px] font-bold text-[#4B5563] uppercase tracking-widest">{DAY_LABEL[d]}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={val}
                  onChange={onChange}
                  maxLength={5}
                  placeholder="HH:mm"
                  pattern="^([01][0-9]|2[0-3]):[0-5][0-9]$"
                  className={`h-8 w-24 px-2 rounded-md border text-sm font-mono-tab text-center bg-white focus:outline-none focus:ring-2 focus:ring-[#0055FF] ${isValid || val === "" ? "border-[#E5E7EB]" : "border-[#DC2626] ring-1 ring-[#DC2626]"}`}
                  data-testid={`late-schedule-${d}`}
                  aria-label={`Batas terlambat ${DAY_LABEL[d]} (format HH:mm 24 jam)`}
                  aria-invalid={val !== "" && !isValid}
                />
              </label>
            );
          })}
        </div>
        <div className="flex justify-end">
          <button
            onClick={saveSchedule}
            disabled={savingSchedule}
            className="h-9 px-4 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-xs font-semibold disabled:opacity-60"
            data-testid="btn-save-late-schedule"
          >
            {savingSchedule ? "Menyimpan…" : "Simpan Pengaturan Global"}
          </button>
        </div>
        {history.length > 0 && (
          <div className="mt-4 border-t border-[#F3F4F6] pt-3" data-testid="global-late-hour-history">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-1.5">Riwayat Perubahan (5 terbaru)</div>
            <ul className="space-y-1">
              {history.slice(0, 5).map((h, i) => {
                let ts = h.at;
                try {
                  const d = new Date(h.at);
                  ts = d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
                } catch { /* keep raw */ }
                // Format nilai lama & baru. Bila `field` mengandung
                // `late_schedule.<day>` → tampilkan label hari. Bila
                // `late_hour` (legacy) → tampilkan H:00. Kompak agar
                // audit-trail tetap rapi.
                const isSch = typeof h.field === "string" && h.field.startsWith("late_schedule.");
                const dayKey = isSch ? h.field.split(".")[1] : null;
                // Fase — Fix 24-jam. Legacy `late_hour` (int) di-zero-pad ke
                // `HH:00` supaya audit-log konsisten dengan format schedule.
                const fmt = (v) => {
                  if (v == null) return "—";
                  if (isSch) return String(v);
                  const n = Number(v);
                  return Number.isFinite(n) ? `${String(n).padStart(2, "0")}:00` : String(v);
                };
                return (
                  <li key={i} className="text-[11px] text-[#4B5563] flex items-center gap-2 flex-wrap" data-testid={`global-late-hour-history-row-${i}`}>
                    <span className="font-mono-tab text-[#9CA3AF]">{ts}</span>
                    <span className="font-semibold">{h.by_name || h.by}</span>
                    <span className="text-[#9CA3AF]">•</span>
                    {isSch && <span className="text-[10px] font-bold uppercase tracking-widest text-[#4B5563]">{DAY_LABEL[dayKey] || dayKey}</span>}
                    <span className="font-mono-tab">
                      {fmt(h.old)} → <span className="font-bold text-[#0055FF]">{fmt(h.new)}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#6B7280]">Master lokasi absensi (Kantor Pusat & Cabang). Radius & GPS accuracy dapat berbeda per lokasi.</div>
        <button
          onClick={() => setEditing({ ...empty })}
          className="h-9 px-3 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold"
          data-testid="btn-add-attendance-loc"
        >
          + Lokasi
        </button>
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="text-left text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] bg-[#F8F9FA]">
            <tr>
              <th className="px-4 py-3">Kode</th>
              <th className="px-4 py-3">Nama</th>
              <th className="px-4 py-3">Koordinat</th>
              <th className="px-4 py-3">Radius</th>
              <th className="px-4 py-3">Akurasi</th>
              <th className="px-4 py-3">Aktif</th>
              <th className="px-4 py-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#9CA3AF]">Memuat…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#9CA3AF]">Belum ada lokasi.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#E5E7EB]" data-testid={`attendance-loc-row-${r.code}`}>
                <td className="px-4 py-3 font-mono-tab font-semibold">{r.code}</td>
                <td className="px-4 py-3">{r.name}</td>
                <td className="px-4 py-3 font-mono-tab text-xs text-[#6B7280]">{Number(r.latitude).toFixed(5)}, {Number(r.longitude).toFixed(5)}</td>
                <td className="px-4 py-3 font-mono-tab">{r.radius_meter}m</td>
                <td className="px-4 py-3 font-mono-tab">≤{r.accuracy_threshold_meter}m</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex h-6 px-2 items-center rounded-full text-[10px] font-bold ${r.active ? "bg-[#ECFDF5] text-[#065F46]" : "bg-[#F3F4F6] text-[#6B7280]"}`}>{r.active ? "AKTIF" : "NONAKTIF"}</span>
                </td>
                <td className="px-4 py-3 text-right space-x-1">
                  <button onClick={() => setEditing({ ...r })} className="h-8 px-2 rounded-md hover:bg-[#F3F4F6] text-xs font-semibold" data-testid={`btn-edit-loc-${r.code}`}>Edit</button>
                  <button onClick={() => del(r.id)} className="h-8 px-2 rounded-md hover:bg-[#FEE2E2] text-xs font-semibold text-[#991B1B]" data-testid={`btn-del-loc-${r.code}`}>Hapus</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="attendance-loc-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">
              {editing?.id ? "Edit Lokasi Absensi" : "Lokasi Absensi Baru"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-1">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Kode</label>
                <input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} className={inputCls} data-testid="loc-code" placeholder="HQ / CABANG-01" />
              </div>
              <div className="md:col-span-1">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Nama</label>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} data-testid="loc-name" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Alamat / Deskripsi</label>
                <input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={inputCls} data-testid="loc-desc" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Latitude</label>
                <input type="number" step="any" value={editing.latitude} onChange={(e) => setEditing({ ...editing, latitude: e.target.value })} className={inputCls} data-testid="loc-lat" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Longitude</label>
                <input type="number" step="any" value={editing.longitude} onChange={(e) => setEditing({ ...editing, longitude: e.target.value })} className={inputCls} data-testid="loc-lng" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Radius (m)</label>
                <input type="number" min={10} max={5000} value={editing.radius_meter} onChange={(e) => setEditing({ ...editing, radius_meter: e.target.value })} className={inputCls} data-testid="loc-radius" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">GPS Accuracy (m)</label>
                <input type="number" min={5} max={1000} value={editing.accuracy_threshold_meter} onChange={(e) => setEditing({ ...editing, accuracy_threshold_meter: e.target.value })} className={inputCls} data-testid="loc-accuracy" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Jam Terlambat (WIB)</label>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={editing.late_hour ?? 8}
                  onChange={(e) => setEditing({ ...editing, late_hour: e.target.value })}
                  className={inputCls}
                  data-testid="loc-late-hour"
                  aria-label="Jam batas keterlambatan (0-23 WIB)"
                />
                <p className="text-[10px] text-[#9CA3AF] mt-1">Check-in ≥ jam ini dianggap TERLAMBAT. Default 8 = 08:00 WIB.</p>
              </div>
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="loc-active" />
                  Aktif
                </label>
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Preview Peta & Radius</label>
                <AttendanceLocationMap
                  lat={editing.latitude}
                  lng={editing.longitude}
                  radiusMeter={editing.radius_meter}
                  onChange={({ lat, lng }) => setEditing({ ...editing, latitude: lat, longitude: lng })}
                />
              </div>
              {editing?.id && (
                <div className="md:col-span-2 border-t border-[#F3F4F6] pt-3" data-testid="loc-late-hour-history">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-1.5">
                    Riwayat Perubahan Jam Terlambat {locHistory.length > 0 && `(${Math.min(locHistory.length, 5)} terbaru)`}
                  </div>
                  {locHistoryLoading ? (
                    <div className="text-[11px] text-[#9CA3AF]">Memuat riwayat…</div>
                  ) : locHistory.length === 0 ? (
                    <div className="text-[11px] text-[#9CA3AF] italic">Belum ada perubahan tercatat untuk lokasi ini.</div>
                  ) : (
                    <ul className="space-y-1">
                      {locHistory.slice(0, 5).map((h, i) => {
                        let ts = h.at;
                        try {
                          const d = new Date(h.at);
                          ts = d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
                        } catch { /* keep raw */ }
                        return (
                          <li key={i} className="text-[11px] text-[#4B5563] flex items-center gap-2 flex-wrap" data-testid={`loc-late-hour-history-row-${i}`}>
                            <span className="font-mono-tab text-[#9CA3AF]">{ts}</span>
                            <span className="font-semibold">{h.by_name || h.by}</span>
                            <span className="text-[#9CA3AF]">•</span>
                            <span className="font-mono-tab">
                              {h.old ?? "—"}:00 → <span className="font-bold text-[#0055FF]">{h.new}:00</span>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button
              onClick={save}
              disabled={saving || !editing?.name || !editing?.code}
              className="h-10 px-5 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold disabled:opacity-50"
              data-testid="save-attendance-loc"
            >
              Simpan Lokasi
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
